import { get } from '@/utils/client';
import { GET_MENTIONS_FEED_MEDIA } from '@/routes/apiEndpoints';
import { useEffect, useMemo, useState } from 'react';
import useSWRInfinite from 'swr/infinite';

const getKey = (pageIndex, previousPageData, activeSPID, mediaID) => {
    // Reached the end
    if (previousPageData && !previousPageData.comments.data) return null;

    // First page, we don't have `previousPageData`
    if (pageIndex === 0) {
        return `${GET_MENTIONS_FEED_MEDIA.replace('%SPID%', activeSPID).replace(
            '%mediaID%',
            mediaID
        )}`;
    }

    // Add the cursor to the API endpoint
    return `${GET_MENTIONS_FEED_MEDIA.replace('%SPID%', activeSPID).replace(
        '%mediaID%',
        mediaID
    )}?cursor=${previousPageData.comments.cursor}`;
};

const useMentionMedia = (
    activeSPID,
    mediaID,
    requestParams = {},
    withPrevNext = true,
    options = {}
) => {
    const [captionReply, setCaptionReply] = useState(null);

    const {
        data,
        size,
        setSize,
        mutate: mutateMedia,
        error: mediaError
    } = useSWRInfinite(
        (pageIndex, previousPageData) => getKey(pageIndex, previousPageData, activeSPID, mediaID),
        (url) =>
            get(
                url,
                withPrevNext ? { ...requestParams, with_pagination: true } : requestParams
            ).then((res) => res.data),
        {
            shouldRetryOnError: false,
            ...options
        }
    );

    const comments = useMemo(() => {
        if (!data) return null;
        return data.reduce(
            (acc, page) => {
                return {
                    cursor: page?.comments?.cursor,
                    data: [...acc.data, ...(page?.comments?.data || [])]
                };
            },
            { cursor: null, data: [] }
        );
    }, [data]);

    useEffect(() => {
        if (data?.[0]?.media?.data?.reply) {
            setCaptionReply({
                ...data[0].media.data.reply,
                is_read: true,
                data: data[0].media.data.reply
            });
        }
    }, [data]);

    const isLoadingInitialData = !data && !mediaError;
    const isLoadingMore = size > 0 && data && typeof data[size - 1] === 'undefined';

    return {
        media: data ? data[0].media : null,
        comments: data ? comments : null,
        commentsIsLoadMore:
            data?.[size - 1]?.comments && typeof data[size - 1].comments?.cursor === 'string', // Check if there is a cursor to active the load more button
        loadMoreComments: () => setSize(size + 1), // Function to load more albums (load more button)
        isLoadingMore,
        isLoadingInitialData, // Check if it's the initial loading (not the load more)
        previousMedia: data?.[size - 1] ? data[size - 1].previous_media : null,
        nextMedia: data?.[size - 1] ? data[size - 1].next_media : null,
        mutateMedia,
        captionReply,
        addReply: (reply) => {
            if (!data) return;
            const newData = [...data];
            newData[size - 1].comments.data = newData[size - 1].comments.data.map((comment) => {
                if (comment.id === reply.comment_id) {
                    return reply.comment;
                }
                return comment;
            });
            mutateMedia(newData);
        },
        mutateMediaComments: (mutation) => {
            const foo = mutation([comments]);
            const { reply } = foo[0].data[0].media.data;
            reply.data = reply;
            reply.is_read = true;
            setCaptionReply(reply);
        },
        mediaError,
        isLoading: !data && !mediaError && !isLoadingMore
    };
};

export default useMentionMedia;
